import React, {useEffect, useLayoutEffect, useRef, useState} from 'react';
import {t} from "../../i18n";

interface SignaturePadProps {
    visible: boolean;
    // 画板标题，由调用方决定（如单据上的双语签名标签）
    title: string;
    // 已有签名的 dataURL，打开时回填到画布
    value: string | null;
    // 只在点「确认」时回传：画板内的笔迹是草稿，左上角返回一律作废、签字框回到打开前的状态。
    // 清空画布后确认回传 null
    onConfirm: (dataUrl: string | null) => void;
    onClose: () => void;
}

const CANVAS_SCALE = 2;
const STROKE_COLOR = '#1A1A1E';
// 画板比单据上的签名框大 6 倍以上，笔迹要按这个比例加粗，缩到单据上才是一条看得清的线：
// 原先的 2.2px 落到成品上不足 1 个像素，会被降采样直接抹掉（iOS 上整条不见）
const STROKE_WIDTH = 5;

// 横屏签名画板：手机竖持时把画板整体旋转 90 度，给更长的书写区
const SignaturePad: React.FC<SignaturePadProps> = ({visible, title, value, onConfirm, onClose}) => {
    // 可见视口尺寸，供旋转板取宽高。
    // 用 visualViewport 而非 100vh/100vw：带动态工具栏的浏览器里 100vh 可能大于可见区域，
    // 旋转后会把板子顶出屏幕。实测的机型上两者一致，这里是防御性的，取不到则退回 window.inner*
    const [boardSize, setBoardSize] = useState<{width: number; height: number} | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
    const drawingRef = useRef<boolean>(false);
    // 当前正在书写的 pointerId：签名只允许单指，第一根手指按下后独占，抬起前忽略其余 pointer
    const activePointerIdRef = useRef<number | null>(null);
    // 用 ref 读取初值：value 每落一笔就变，若进 effect 依赖会导致画布被反复重建、笔迹清空
    const valueRef = useRef<string | null>(value);
    valueRef.current = value;
    // 画板内的草稿：打开时从 value 起步，每笔抬手/清除只更新这里，确认时才整体回传
    const draftRef = useRef<string | null>(value);

    // 量容器真实尺寸；转屏/工具栏收起都会变，跟着重新量
    useLayoutEffect(() => {
        if (!visible) {
            setBoardSize(null);
            return;
        }
        const measure = () => {
            const vv = window.visualViewport;
            setBoardSize({
                width: Math.round(vv?.width ?? window.innerWidth),
                height: Math.round(vv?.height ?? window.innerHeight),
            });
        };
        measure();
        const vv = window.visualViewport;
        window.addEventListener('resize', measure);
        window.addEventListener('orientationchange', measure);
        // 工具栏收放、键盘弹出都只触发 visualViewport 的事件，不触发 window.resize
        vv?.addEventListener('resize', measure);
        vv?.addEventListener('scroll', measure);
        return () => {
            window.removeEventListener('resize', measure);
            window.removeEventListener('orientationchange', measure);
            vv?.removeEventListener('resize', measure);
            vv?.removeEventListener('scroll', measure);
        };
    }, [visible]);

    // 打开时初始化画布并回填已有签名。依赖里带上尺寸：首帧量到真实值后画布要按新尺寸重建，
    // 否则 canvas 的分辨率停留在旧尺寸，笔迹位置会偏
    useEffect(() => {
        if (!visible || !boardSize) {
            return;
        }
        const el = canvasRef.current;
        if (!el) {
            return;
        }
        // 按 2 倍分辨率绘制，避免高清屏下笔迹发虚
        el.width = el.offsetWidth * CANVAS_SCALE;
        el.height = el.offsetHeight * CANVAS_SCALE;
        const ctx = el.getContext('2d');
        if (!ctx) {
            return;
        }
        ctx.scale(CANVAS_SCALE, CANVAS_SCALE);
        ctx.strokeStyle = STROKE_COLOR;
        ctx.lineWidth = STROKE_WIDTH;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctxRef.current = ctx;
        drawingRef.current = false;
        activePointerIdRef.current = null;

        const saved = valueRef.current;
        draftRef.current = saved;
        if (saved) {
            const img = new Image();
            img.onload = () => ctx.drawImage(img, 0, 0, el.offsetWidth, el.offsetHeight);
            img.src = saved;
        }
    }, [visible, boardSize]);

    // 画布父容器 rotate(90deg)，需把屏幕坐标反旋转回画布本地坐标
    const localPoint = (e: React.PointerEvent<HTMLCanvasElement>) => {
        const el = e.currentTarget;
        const rect = el.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        const dx = e.clientX - centerX;
        const dy = e.clientY - centerY;
        return {x: dy + el.offsetWidth / 2, y: -dx + el.offsetHeight / 2};
    };

    // 画布是否有笔迹：全透明像素视为空签名，避免空白也算已签
    const canvasHasInk = (): boolean => {
        const el = canvasRef.current;
        const ctx = ctxRef.current;
        if (!el || !ctx) {
            return false;
        }
        try {
            const {data} = ctx.getImageData(0, 0, el.width, el.height);
            // 每像素 RGBA 四字节，只查 alpha 通道
            for (let i = 3; i < data.length; i += 4) {
                if (data[i] !== 0) {
                    return true;
                }
            }
        } catch (error) {
            console.error('Failed to read signature canvas:', error);
        }
        return false;
    };

    const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
        const ctx = ctxRef.current;
        if (!ctx) {
            return;
        }
        // 已有手指在写就忽略后来的：多指同时按下时，每根手指的 pointerdown 都会 beginPath+moveTo
        // 重置同一条路径，接着任一根手指移动就把上一个落点连成直线——两指画一条线、三指画个三角形。
        // 签名本来也只该单指，故第一根手指全程独占，抬起前其余 pointer 一律不参与
        if (activePointerIdRef.current !== null) {
            return;
        }
        e.preventDefault();
        activePointerIdRef.current = e.pointerId;
        // 捕获后手指划出画布边界仍能收到事件，抬手时也稳定收到 pointerup（否则会漏掉那一笔的回传）
        e.currentTarget.setPointerCapture?.(e.pointerId);
        drawingRef.current = true;
        const point = localPoint(e);
        ctx.beginPath();
        ctx.moveTo(point.x, point.y);
    };

    const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
        const ctx = ctxRef.current;
        if (!drawingRef.current || !ctx || e.pointerId !== activePointerIdRef.current) {
            return;
        }
        e.preventDefault();
        const point = localPoint(e);
        ctx.lineTo(point.x, point.y);
        ctx.stroke();
    };

    // pointercancel 也走这里：系统手势/误触抑制会直接取消当前 pointer 而不发 pointerup，
    // 不处理的话 activePointerId 永远不释放，之后整块画板就再也写不了字
    const handlePointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
        if (e.pointerId !== activePointerIdRef.current) {
            return;
        }
        e.currentTarget.releasePointerCapture?.(e.pointerId);
        activePointerIdRef.current = null;
        if (!drawingRef.current) {
            return;
        }
        drawingRef.current = false;
        const el = canvasRef.current;
        if (el && canvasHasInk()) {
            draftRef.current = el.toDataURL('image/png');
        }
    };

    const handleClear = () => {
        const el = canvasRef.current;
        const ctx = ctxRef.current;
        if (el && ctx) {
            ctx.clearRect(0, 0, el.width, el.height);
        }
        draftRef.current = null;
    };

    const handleConfirm = () => {
        onConfirm(draftRef.current);
        onClose();
    };

    if (!visible) {
        return null;
    }

    // 外层也按可见视口尺寸摆而不用 inset-0：某些浏览器里 fixed 容器贴的是布局视口，
    // 内层 top/left 50% 的居中基准会跟着偏
    return (
        <div
            className="fixed left-0 top-0 overflow-hidden"
            style={{
                width: boardSize ? `${boardSize.width}px` : '100vw',
                height: boardSize ? `${boardSize.height}px` : '100vh',
                background: 'rgba(20, 20, 26, 0.92)',
                zIndex: 250,
            }}
        >
            {/* 旋转 90 度：宽高与容器互换，绕自身中心旋转后仍居中 */}
            <div
                className="absolute flex flex-col overflow-hidden bg-white"
                style={{
                    top: '50%',
                    left: '50%',
                    width: boardSize ? `${boardSize.height}px` : '100vh',
                    height: boardSize ? `${boardSize.width}px` : '100vw',
                    transform: 'translate(-50%, -50%) rotate(90deg)',
                }}
            >
                <div className="flex shrink-0 items-center gap-2.5 border-b border-gray-100 px-5 py-3">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="shrink-0" onClick={onClose}>
                        <path d="M13 2L4 10l9 8" stroke="#1A1A1E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <div className="flex-1 text-center text-base font-bold mr-6">{title}</div>
                </div>
                <div className="flex min-h-0 flex-1 gap-4 px-5 py-4">
                    {/* min-w-0 不能省：初始化时 el.width = offsetWidth * 2 会把 canvas 的
                        intrinsic 宽度改成 1060px，而 flex item 默认 min-width:auto 取的正是这个值，
                        于是这一行的最小宽度撑到 1060+128，把右侧按钮列挤出板子、旋转后落到屏幕外 */}
                    <canvas
                        ref={canvasRef}
                        className="min-w-0 flex-1 rounded-xl"
                        style={{background: '#F1EEFB', border: '1.5px solid #C9BEF0', touchAction: 'none'}}
                        onPointerDown={handlePointerDown}
                        onPointerMove={handlePointerMove}
                        onPointerUp={handlePointerUp}
                        onPointerCancel={handlePointerUp}
                    />
                    <div className="flex w-32 shrink-0 flex-col justify-end gap-2.5">
                        <div
                            className="rounded-full bg-gray-100 py-3 text-center text-sm font-semibold text-gray-600"
                            onClick={handleClear}
                        >
                            {t('components.signaturePad.clearAndResign')}
                        </div>
                        <div
                            className="rounded-full py-3 text-center text-sm font-semibold text-white"
                            style={{background: 'var(--adm-color-primary)'}}
                            onClick={handleConfirm}
                        >
                            {t('components.signaturePad.confirm')}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SignaturePad;
